import type { Theme } from '../../contract.js';
import { cartographicLight, cartographicDark } from './variants.js';

/**
 * Cartographic theme — earthy palette + Mapbox Outdoors basemap.
 *
 * Pre-redesign, Cartographic inherited Default's variants
 * entirely and was visually identical to the Initiative theme
 * — only the mapStyle distinguished them. Post-redesign, the
 * family has a bespoke earthy palette (parchment / cream / moss
 * for Light, espresso / amber for Dark) and the Outdoors
 * basemap on both modes, giving "the explorer / hiking / nature"
 * mental model a coherent visual identity.
 *
 * ChargingAnimation resolves this family to the interactive green/gold
 * contour terrain surface, in both light and dark mode.
 */
export const cartographicTheme: Theme = {
  id: 'cartographic',
  name: 'Cartographic',
  description: 'Earthy palette with Mapbox Outdoors basemap for the explorer.',
  // Picker swatches — cream / moss / amber capture the earthy
  // identity in 3 chips. The picker SwatchRow reads variant
  // tokens directly (accent / charging / surface) so the per-row
  // chip preview is driven by the variant tokens; this top-level
  // array is read by packages/ui DriverThemePicker for catalogue
  // display and stays in sync with the new identity.
  swatches: ['#F4EDE0', '#5C7C3A', '#C99A4B'],
  variants: { light: cartographicLight, dark: cartographicDark },
  unlockedByDefault: true,
  costPoints: 0,
  // Mapbox Outdoors v12 — the canonical hiking / topographic /
  // terrain-aware basemap. v12 is the current major version (v11
  // is legacy). The v12 style serves both light and dark
  // contexts via Mapbox's internal style switching; we point
  // both modes at the same style URL because Outdoors doesn't
  // ship a separate dark-mode variant the way Streets does.
  mapStyle: {
    light: 'mapbox://styles/mapbox/outdoors-v12',
    dark: 'mapbox://styles/mapbox/outdoors-v12',
  },
};
